import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Award, FileText, Building2 } from "lucide-react";
import api from "@/lib/api";
import { SeoHead } from "@/components/public/SeoHead";
import { JsonLd } from "@/components/public/JsonLd";
import { claimTypeLabel, claimTypeDescription } from "@/lib/certificationTypes";
import { DetailHeader, DetailBody } from "@/pages/public/MaterialDetailPage";

export default function CertificationDetailPage() {
  const { slug } = useParams();
  const { data: cert, error, isLoading } = useQuery({
    queryKey: ["public-certification", slug],
    queryFn: async () => (await api.get(`/public/materials-certifications/${slug}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-muted_text">Loading…</div>;
  if (error || !cert) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-14 text-center">
        <h1 className="font-serif text-4xl mb-4">Certification not found</h1>
        <p className="text-muted_text">This certification is either unpublished or doesn't exist.</p>
      </div>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "https://sustainablycurated.com";
  const url = `${origin}/materials-certifications/${slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      name: cert.name,
      description: cert.short_description || cert.full_description?.slice(0, 280),
      url,
      sameAs: cert.external_link || undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Certifications", item: origin + "/materials-certifications" },
        { "@type": "ListItem", position: 3, name: cert.name, item: url },
      ],
    },
  ];

  const claimLabel = claimTypeLabel(cert.main_claim_type);
  const claimDesc = claimTypeDescription(cert.main_claim_type);

  const quickFacts = [
    cert.organization && { label: "Issuing organization", value: cert.organization },
    claimLabel && { label: "Claim type", value: claimLabel },
    cert.external_link && { label: "Official site", value: hostname(cert.external_link) },
  ].filter(Boolean);

  const sections = [
    cert.full_description && { icon: FileText, title: "About this certification", body: cert.full_description },
    claimDesc && { icon: Award, title: `What "${claimLabel}" means`, body: claimDesc },
  ].filter(Boolean);

  const ctaPrimary = cert.external_link ? (
    <a
      href={cert.external_link}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex w-full items-center justify-center gap-2 text-xs uppercase tracking-wider bg-forest text-ivory px-4 py-2.5 hover:bg-forest/90 rounded-full"
      data-testid="cert-external-link"
    >
      Visit official site <ExternalLink className="w-3.5 h-3.5" />
    </a>
  ) : null;

  return (
    <article className="bg-ivory" data-testid="certification-detail-page">
      <SeoHead
        title={cert.seo_title || `${cert.name} — Certifications — Sustainably Curated`}
        description={cert.seo_description || cert.short_description}
        path={`/materials-certifications/${slug}`}
      />
      <JsonLd data={jsonLd} />

      <DetailHeader
        eyebrow="Certification"
        title={cert.name}
        subtitle={cert.organization ? <><Building2 className="w-3.5 h-3.5 inline-block mr-1.5 -mt-px text-muted_text" />{cert.organization}</> : null}
        lead={cert.short_description}
        badge={claimLabel ? <ClaimTypeBadge label={claimLabel} /> : <Award className="w-8 h-8 text-forest" />}
        accentLine="bg-forest"
        backLabel="All certifications"
        backTo="/materials-certifications"
      />

      <DetailBody
        sections={sections}
        quickFacts={quickFacts}
        ctaPrimary={ctaPrimary}
      />
    </article>
  );
}

function ClaimTypeBadge({ label }) {
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 border border-forest text-forest font-medium tracking-wider text-xs uppercase rounded-full" data-testid="cert-claim-type-badge">
      <Award className="w-3.5 h-3.5" />
      {label}
    </div>
  );
}

function hostname(u) {
  try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; }
}
